import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import TopNav from './TopNav';
import api from '../services/api';
import { getTradingPreferences, updateTradingPreferences, getNotificationPreferences, updateNotificationPreferences, changePassword, deleteUserAccount, getProfile, updateProfile, getPDTStatus } from '../services/api';

/**
 * Settings Page
 * Allows users to manage their account preferences and settings
 */
const RISK_TOLERANCE_DETAILS: Record<'LOW' | 'MEDIUM' | 'HIGH', { description: string; maxPositions: string; positionCap: string; orderSize: string }> = {
  LOW: {
    description: 'Conservative: smaller positions, tighter stops, fewer holdings (12 max)',
    maxPositions: '12',
    positionCap: '$3,000',
    orderSize: '$800'
  },
  MEDIUM: {
    description: 'Balanced: moderate size, standard stops, diversified holdings (16 max)',
    maxPositions: '16',
    positionCap: '$5,000',
    orderSize: '$1,200'
  },
  HIGH: {
    description: 'Growth: larger size, wider stops, more opportunities (20 max)',
    maxPositions: '20',
    positionCap: '$7,000',
    orderSize: '$1,500'
  }
};

const Settings: React.FC = () => {
  const { user } = useAuth();
  const [hasActiveAlpacaAccount, setHasActiveAlpacaAccount] = useState<boolean>(false);
  
  // Define tabs array before it's used in getInitialTab
  const allTabs = [
    { id: 'profile', name: 'Profile', icon: '' },
    { id: 'security', name: 'Security', icon: '' },
    { id: 'notifications', name: 'Notifications', icon: '' },
    { id: 'trading', name: 'Trading', icon: '' },
    { id: 'api', name: 'API Keys', icon: '' }
  ];
  
  // Persist active tab to localStorage and URL
  const getInitialTab = (): string => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabFromUrl = urlParams.get('tab');
    if (tabFromUrl && allTabs.some(t => t.id === tabFromUrl)) {
      return tabFromUrl;
    }
    const savedTab = localStorage.getItem('settingsActiveTab');
    if (savedTab && allTabs.some(t => t.id === savedTab)) {
      return savedTab;
    }
    return 'profile';
  };
  
  const [activeTab, setActiveTab] = useState<string>(getInitialTab());
  
  // Update URL and localStorage when tab changes
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    localStorage.setItem('settingsActiveTab', tab);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tab);
    window.history.replaceState({}, '', url.toString());
  };
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [biweeklyRotation, setBiweeklyRotation] = useState<any>(null);
  const [isLoadingBiweekly, setIsLoadingBiweekly] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const checkAlpacaAccounts = async () => {
    try {
      const response = await api.get('/accounts');
      const accountsData = response.data?.data || [];
      setAccounts(accountsData);
      const hasActive = accountsData.some((acc: any) => acc.status === 'ACTIVE' && acc.alpacaKey && acc.alpacaSecret);
      setHasActiveAlpacaAccount(hasActive);
      
      // Set default selected account
      if (accountsData.length > 0 && !selectedAccountId) {
        setSelectedAccountId(accountsData[0].id);
      }
    } catch (error) {
      console.error('Failed to check Alpaca accounts:', error);
    }
  };

  const fetchBiweeklyRotationSettings = async () => {
    if (!selectedAccountId) return;
    
    setIsLoadingBiweekly(true);
    try {
      const response = await api.get(`/preferences/biweekly-rotation?accountId=${selectedAccountId}`);
      if (response.data?.success) {
        setBiweeklyRotation(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch biweekly rotation settings:', error);
      setMessage({ type: 'error', text: 'Failed to load biweekly rotation settings' });
    } finally {
      setIsLoadingBiweekly(false);
    }
  };

  const updateBiweeklyRotationPreference = async (enabled: boolean) => {
    if (!selectedAccountId) return;
    
    setIsLoading(true);
    try {
      const response = await api.put('/preferences/biweekly-rotation', {
        accountId: selectedAccountId,
        enabled
      });
      if (response.data?.success) {
        setBiweeklyRotation(response.data.data);
        setMessage({ type: 'success', text: 'Biweekly rotation preference updated successfully' });
      }
    } catch (error) {
      console.error('Failed to update biweekly rotation preference:', error);
      setMessage({ type: 'error', text: 'Failed to update biweekly rotation preference' });
    } finally {
      setIsLoading(false);
    }
  };
  
  // PDT status state
  const [pdtStatus, setPdtStatus] = useState<{
    isPDTQualified: boolean;
    equity: number;
    minRequired: number;
    accountId: string | null;
    accountName: string | null;
    statusText: string;
    error?: string;
  } | null>(null);
  const [isLoadingPDTStatus, setIsLoadingPDTStatus] = useState(false);

  // Trading preferences state (preset-based)
  const [tradingPreferences, setTradingPreferences] = useState({
    paperTradingOnly: true,
    checkInterval: 20,
    riskTolerancePreset: 'MEDIUM' as 'LOW' | 'MEDIUM' | 'HIGH',
    maxPositionsPreset: 'MEDIUM' as 'LOW' | 'MEDIUM' | 'HIGH',
    maxPositionValuePreset: 'MEDIUM' as 'LOW' | 'MEDIUM' | 'HIGH',
    profitTargetPreset: 'MEDIUM' as 'LOW' | 'MEDIUM' | 'HIGH',
    stopLossPreset: 'MEDIUM' as 'LOW' | 'MEDIUM' | 'HIGH',
    defaultOrderSizePreset: 'MEDIUM' as 'LOW' | 'MEDIUM' | 'HIGH',
    resolvedValues: {
      maxPositions: 16,
      maxPositionValueUsd: 5000,
      profitTargetPct: 8.0,
      stopLossPct: 2.5,
      defaultOrderSizeUsd: 2500,
      maxPositionPctPerSymbol: 10.0,
      perSymbolCapUsd: 5000
    },
    presetValues: {
      profitTargetPct: { LOW: 6.0, MEDIUM: 5.0, HIGH: 4.0 },
      stopLossPct: { LOW: 2.0, MEDIUM: 2.5, HIGH: 3.0 },
      maxPositions: { LOW: 12, MEDIUM: 16, HIGH: 20 },
      maxPositionValueUsd: { LOW: 3000, MEDIUM: 5000, HIGH: 7000 },
      defaultOrderSizeUsd: { LOW: 800, MEDIUM: 1200, HIGH: 1500 }
    }
  });

  // Notification preferences state
  const [notificationPreferences, setNotificationPreferences] = useState({
    emailNotifications: true,
    orderNotifications: true,
    tradeNotifications: true,
    weeklySummary: true,
    systemAlerts: true,
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Profile state
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    gender: null as 'MALE' | 'FEMALE' | 'OTHERS' | 'PREFER_NOT_TO_SAY' | null,
    address: '',
    bio: '',
    phoneNumber: '',
    dateOfBirth: '',
    country: '',
    timezone: '',
    createdAt: '',
  });

  // Load PDT status when trading tab is active
  useEffect(() => {
    if (activeTab === 'trading' && user?.role !== 'ADMIN') {
      loadPDTStatus();
    }
  }, [activeTab, user]);

  // Load preferences on component mount
  useEffect(() => {
    checkAlpacaAccounts();
    loadProfile();
  }, []);

  const loadPDTStatus = async () => {
    setIsLoadingPDTStatus(true);
    try {
      const response = await getPDTStatus();
      if (response.success && response.data) {
        setPdtStatus(response.data);
      } else {
        setPdtStatus(null);
      }
    } catch (error) {
      console.error('Failed to load PDT status:', error);
      setPdtStatus(null);
    } finally {
      setIsLoadingPDTStatus(false);
    }
  };

  const loadProfile = async () => {
    try {
      const response = await getProfile();
      if (response.success && response.data) {
        // Format date of birth for input field (YYYY-MM-DD)
        const dateOfBirth = response.data.dateOfBirth 
          ? new Date(response.data.dateOfBirth).toISOString().split('T')[0]
          : '';
        
        // Get default timezone from browser if not set
        const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        setProfileData({
          firstName: response.data.firstName || '',
          lastName: response.data.lastName || '',
          gender: response.data.gender || null,
          address: response.data.address || '',
          bio: response.data.bio || '',
          phoneNumber: response.data.phoneNumber || '',
          dateOfBirth: dateOfBirth || '',
          country: response.data.country || '',
          timezone: response.data.timezone || defaultTimezone,
          createdAt: response.data.createdAt || '',
        });
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      // Set default from user context if available
      if (user) {
        setProfileData(prev => ({
          ...prev,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }));
      }
    }
  };

  useEffect(() => {
    if (selectedAccountId) {
      fetchBiweeklyRotationSettings();
    }
  }, [selectedAccountId]);

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        console.log('Loading preferences...');
        console.log('User:', user?.email, 'ID:', user?.id, 'Role:', user?.role);
        
        // For admin users, only load notification preferences
        if (user?.role === 'ADMIN') {
          console.log('Admin user detected, loading only notification preferences...');
          const notificationResponse = await getNotificationPreferences();
          console.log('Notification response:', notificationResponse);
          
          if (notificationResponse.success && notificationResponse.data) {
            setNotificationPreferences(notificationResponse.data);
          }
        } else {
          // For regular users, load both trading and notification preferences
          console.log('Regular user detected, loading all preferences...');
          const [tradingResponse, notificationResponse] = await Promise.all([
            getTradingPreferences(),
            getNotificationPreferences(),
          ]);
          
          console.log('Trading response:', tradingResponse);
          console.log('Notification response:', notificationResponse);
          
          if (tradingResponse.success && tradingResponse.data) {
            console.log(' LOAD: Loaded trading preferences:', tradingResponse.data);
            console.log(' LOAD: Risk tolerance preset loaded:', tradingResponse.data.riskTolerancePreset);
            console.log(' LOAD: Previous state risk tolerance preset:', tradingPreferences.riskTolerancePreset);
            
            // Only update state if we actually got data
            setTradingPreferences(tradingResponse.data);
            
            console.log(' LOAD: State updated with loaded preferences');
          } else if (tradingResponse.error && typeof tradingResponse.error === 'string' && tradingResponse.error.includes('not found')) {
            // 404 - user doesn't have preferences yet, use defaults (already set in state)
            console.log(' LOAD: No trading preferences found - using default values');
          } else {
            console.error(' LOAD: Failed to load trading preferences:', tradingResponse);
            console.error(' LOAD: Error details:', tradingResponse.error);
          }
          
          if (notificationResponse.success && notificationResponse.data) {
            setNotificationPreferences(notificationResponse.data);
          }
        }
      } catch (error) {
        console.error('Failed to load preferences:', error);
        console.error('Error details:', error);
      } finally {
        setIsLoadingPreferences(false);
      }
    };

    // Only load if user is authenticated and has an ID
    if (user && user.id) {
      console.log('User authenticated, loading preferences...');
      // Add a small delay to ensure authentication is fully processed
      setTimeout(() => {
        loadPreferences();
      }, 100);
    } else {
      console.log('User not authenticated or missing ID, skipping preferences load');
      setIsLoadingPreferences(false);
    }
  }, [user]);

  const handleSave = async (section: string) => {
    setIsLoading(true);
    setMessage(null);
    
    try {
      if (section === 'trading') {
        console.log('SAVE: Saving trading preferences:', tradingPreferences);
        console.log('SAVE: Risk tolerance preset being saved:', tradingPreferences.riskTolerancePreset);
        console.log('SAVE: About to call updateTradingPreferences...');
        
        const response = await updateTradingPreferences(tradingPreferences);
        
        console.log('SAVE: Save response received:', response);
        console.log('SAVE: Response type:', typeof response);
        console.log('SAVE: Response keys:', Object.keys(response || {}));
        if (response.success) {
          console.log(' SAVE: Preferences saved successfully');
          setMessage({ type: 'success', text: 'Trading preferences saved successfully!' });
          
          // Immediately reload to verify the save worked
          console.log(' SAVE: Reloading preferences to verify...');
          setTimeout(async () => {
            try {
              const verifyResponse = await getTradingPreferences();
              console.log(' VERIFY: Loaded after save:', verifyResponse.data);
              console.log(' VERIFY: Risk tolerance after save:', verifyResponse.data?.riskTolerancePreset);
            } catch (error) {
              console.error(' VERIFY: Failed to reload for verification:', error);
            }
          }, 500);
          
        } else {
          const errorMessage = typeof response.error === 'string' ? response.error : (response.error as any)?.error?.message || 'Failed to save trading preferences';
          throw new Error(errorMessage);
        }
      } else if (section === 'notifications') {
        const response = await updateNotificationPreferences(notificationPreferences);
        if (response.success) {
          setMessage({ type: 'success', text: 'Notification preferences saved successfully!' });
        } else {
          const errorMessage = typeof response.error === 'string' ? response.error : (response.error as any)?.error?.message || 'Failed to save notification preferences';
          throw new Error(errorMessage);
        }
      } else if (section === 'security') {
        // Validate password data
        if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
          throw new Error('All password fields are required');
        }
        
        if (passwordData.newPassword !== passwordData.confirmPassword) {
          throw new Error('New passwords do not match');
        }
        
        if (passwordData.newPassword.length < 8) {
          throw new Error('New password must be at least 8 characters long');
        }
        
        if (!user?.email) {
          throw new Error('User email not found');
        }
        
        const response = await changePassword({
          email: user.email,
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        });
        
        if (response.success) {
          setMessage({ type: 'success', text: 'Password changed successfully!' });
          // Clear the form
          setPasswordData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          });
        } else {
          const errorMessage = typeof response.error === 'string' ? response.error : (response.error as any)?.error?.message || 'Failed to change password';
          throw new Error(errorMessage);
        }
      } else if (section === 'profile') {
        // Format dateOfBirth for API (ISO string or null)
        const dateOfBirthISO = profileData.dateOfBirth 
          ? new Date(profileData.dateOfBirth).toISOString()
          : null;
        
        const response = await updateProfile({
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          gender: profileData.gender,
          address: profileData.address || null,
          bio: profileData.bio || null,
          phoneNumber: profileData.phoneNumber || null,
          dateOfBirth: dateOfBirthISO,
          country: profileData.country || null,
          timezone: profileData.timezone || null,
        });
        
        if (response.success) {
          setMessage({ type: 'success', text: 'Profile updated successfully!' });
          // Update user context if needed
          if (response.data) {
            const dateOfBirth = response.data.dateOfBirth 
              ? new Date(response.data.dateOfBirth).toISOString().split('T')[0]
              : '';
            
            setProfileData({
              firstName: response.data.firstName || '',
              lastName: response.data.lastName || '',
              gender: response.data.gender || null,
              address: response.data.address || '',
              bio: response.data.bio || '',
              phoneNumber: response.data.phoneNumber || '',
              dateOfBirth: dateOfBirth || '',
              country: response.data.country || '',
              timezone: response.data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
              createdAt: response.data.createdAt || '',
            });
          }
        } else {
          const errorMessage = typeof response.error === 'string' ? response.error : (response.error as any)?.error?.message || 'Failed to update profile';
          throw new Error(errorMessage);
        }
      } else {
        // TODO: Implement other sections
        setMessage({ type: 'error', text: `${section} settings not yet implemented` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to save ${section} settings: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setIsLoading(false);
    }
  };

  // Filter tabs based on user role - admin users don't need trading and API keys tabs
  const tabs = user?.role === 'ADMIN' 
    ? allTabs.filter(tab => !['trading', 'api'].includes(tab.id))
    : allTabs;

  // Validate and redirect if saved tab is not available for current user role
  useEffect(() => {
    if (user) {
      // Check if current active tab is available for this user's role
      const isTabAvailable = tabs.some(tab => tab.id === activeTab);
      if (!isTabAvailable) {
        // Tab is not available (e.g., admin trying to access trading tab)
        // Redirect to first available tab (usually 'profile')
        const firstAvailableTab = tabs[0]?.id || 'profile';
        handleTabChange(firstAvailableTab);
      }
    }
  }, [user, activeTab, tabs]);

  return (
    <div className="min-h-screen bg-neutral-950">
      <TopNav />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
                <p className="text-neutral-400">
                  Manage your account preferences and security settings
                </p>
              </div>
              <button
                onClick={() => window.location.reload()}
                className="bg-neutral-700 hover:bg-neutral-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Message */}
          {message && (
            <div className={`mb-6 px-4 py-3 rounded-lg ${
              message.type === 'success' 
                ? 'bg-green-900/20 border border-green-500/50 text-green-400'
                : 'bg-red-900/20 border border-red-500/50 text-red-400'
            }`}>
              {message.text}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-4">
                <nav className="space-y-2">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                        activeTab === tab.id
                          ? 'bg-cyan-600 text-white'
                          : 'text-neutral-400 hover:text-white hover:bg-neutral-800'
                      }`}
                    >
                      <span className="text-lg">{tab.icon}</span>
                      <span className="font-medium">{tab.name}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-6">
                {activeTab === 'profile' && (
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-6">Profile Settings</h2>
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-neutral-300 mb-2">
                            Email Address
                          </label>
                          <input
                            type="email"
                            value={user?.email || ''}
                            disabled
                            className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-neutral-400 cursor-not-allowed"
                          />
                          <p className="text-sm text-neutral-500 mt-1">Email cannot be changed</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-neutral-300 mb-2">
                            Display Name
                          </label>
                          <input
                            type="text"
                            value={`${profileData.firstName} ${profileData.lastName}`.trim() || ''}
                            disabled
                            className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-neutral-400 cursor-not-allowed"
                          />
                          <p className="text-sm text-neutral-500 mt-1">Display name is based on your first and last name from sign up</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-neutral-300 mb-2">
                            First Name
                          </label>
                          <input
                            type="text"
                            value={profileData.firstName}
                            onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
                            placeholder="Enter your first name"
                            className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-neutral-300 mb-2">
                            Last Name
                          </label>
                          <input
                            type="text"
                            value={profileData.lastName}
                            onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
                            placeholder="Enter your last name"
                            className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-300 mb-2">
                          Gender
                        </label>
                        <select
                          value={profileData.gender || ''}
                          onChange={(e) => setProfileData(prev => ({ ...prev, gender: e.target.value as any || null }))}
                          className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        >
                          <option value="">Select gender</option>
                          <option value="MALE">Male</option>
                          <option value="FEMALE">Female</option>
                          <option value="OTHERS">Others</option>
                          <option value="PREFER_NOT_TO_SAY">Do not wish to identify</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-300 mb-2">
                          Address
                        </label>
                        <textarea
                          rows={3}
                          value={profileData.address}
                          onChange={(e) => setProfileData(prev => ({ ...prev, address: e.target.value }))}
                          placeholder="Enter your address"
                          className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-neutral-300 mb-2">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            value={profileData.phoneNumber}
                            onChange={(e) => setProfileData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                            placeholder="+1 (555) 123-4567"
                            className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          />
                          <p className="text-sm text-neutral-500 mt-1">For account recovery and notifications</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-neutral-300 mb-2">
                            Date of Birth
                          </label>
                          <input
                            type="date"
                            value={profileData.dateOfBirth}
                            onChange={(e) => setProfileData(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                            max={new Date().toISOString().split('T')[0]}
                            className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          />
                          <p className="text-sm text-neutral-500 mt-1">For compliance and verification</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-neutral-300 mb-2">
                            Country/Region
                          </label>
                          <select
                            value={profileData.country}
                            onChange={(e) => setProfileData(prev => ({ ...prev, country: e.target.value }))}
                            className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          >
                            <option value="">Select country</option>
                            <option value="US">United States</option>
                            <option value="CA">Canada</option>
                            <option value="GB">United Kingdom</option>
                            <option value="AU">Australia</option>
                            <option value="DE">Germany</option>
                            <option value="FR">France</option>
                            <option value="IT">Italy</option>
                            <option value="ES">Spain</option>
                            <option value="NL">Netherlands</option>
                            <option value="BE">Belgium</option>
                            <option value="CH">Switzerland</option>
                            <option value="AT">Austria</option>
                            <option value="SE">Sweden</option>
                            <option value="NO">Norway</option>
                            <option value="DK">Denmark</option>
                            <option value="FI">Finland</option>
                            <option value="IE">Ireland</option>
                            <option value="NZ">New Zealand</option>
                            <option value="SG">Singapore</option>
                            <option value="HK">Hong Kong</option>
                            <option value="JP">Japan</option>
                            <option value="KR">South Korea</option>
                            <option value="CN">China</option>
                            <option value="IN">India</option>
                            <option value="BR">Brazil</option>
                            <option value="MX">Mexico</option>
                            <option value="AR">Argentina</option>
                            <option value="ZA">South Africa</option>
                            <option value="AE">United Arab Emirates</option>
                            <option value="SA">Saudi Arabia</option>
                            <option value="IL">Israel</option>
                            <option value="TR">Turkey</option>
                            <option value="RU">Russia</option>
                            <option value="PL">Poland</option>
                            <option value="CZ">Czech Republic</option>
                            <option value="GR">Greece</option>
                            <option value="PT">Portugal</option>
                            <option value="OTHER">Other</option>
                          </select>
                          <p className="text-sm text-neutral-500 mt-1">For localization and compliance</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-neutral-300 mb-2">
                            Timezone
                          </label>
                          <select
                            value={profileData.timezone}
                            onChange={(e) => setProfileData(prev => ({ ...prev, timezone: e.target.value }))}
                            className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          >
                            <option value="">Select timezone</option>
                            <optgroup label="Americas">
                              <option value="America/New_York">Eastern Time (US & Canada)</option>
                              <option value="America/Chicago">Central Time (US & Canada)</option>
                              <option value="America/Denver">Mountain Time (US & Canada)</option>
                              <option value="America/Los_Angeles">Pacific Time (US & Canada)</option>
                              <option value="America/Toronto">Toronto</option>
                              <option value="America/Vancouver">Vancouver</option>
                              <option value="America/Mexico_City">Mexico City</option>
                              <option value="America/Sao_Paulo">São Paulo</option>
                              <option value="America/Buenos_Aires">Buenos Aires</option>
                            </optgroup>
                            <optgroup label="Europe">
                              <option value="Europe/London">London</option>
                              <option value="Europe/Paris">Paris</option>
                              <option value="Europe/Berlin">Berlin</option>
                              <option value="Europe/Rome">Rome</option>
                              <option value="Europe/Madrid">Madrid</option>
                              <option value="Europe/Amsterdam">Amsterdam</option>
                              <option value="Europe/Brussels">Brussels</option>
                              <option value="Europe/Zurich">Zurich</option>
                              <option value="Europe/Vienna">Vienna</option>
                              <option value="Europe/Stockholm">Stockholm</option>
                              <option value="Europe/Oslo">Oslo</option>
                              <option value="Europe/Copenhagen">Copenhagen</option>
                              <option value="Europe/Helsinki">Helsinki</option>
                              <option value="Europe/Dublin">Dublin</option>
                              <option value="Europe/Moscow">Moscow</option>
                            </optgroup>
                            <optgroup label="Asia Pacific">
                              <option value="Asia/Tokyo">Tokyo</option>
                              <option value="Asia/Shanghai">Shanghai</option>
                              <option value="Asia/Hong_Kong">Hong Kong</option>
                              <option value="Asia/Singapore">Singapore</option>
                              <option value="Asia/Seoul">Seoul</option>
                              <option value="Asia/Dubai">Dubai</option>
                              <option value="Asia/Kolkata">Mumbai, New Delhi</option>
                              <option value="Asia/Jakarta">Jakarta</option>
                              <option value="Asia/Bangkok">Bangkok</option>
                              <option value="Australia/Sydney">Sydney</option>
                              <option value="Australia/Melbourne">Melbourne</option>
                              <option value="Pacific/Auckland">Auckland</option>
                            </optgroup>
                          </select>
                          <p className="text-sm text-neutral-500 mt-1">For scheduling and notifications</p>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-300 mb-2">
                          Bio
                        </label>
                        <textarea
                          rows={4}
                          value={profileData.bio}
                          onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
                          placeholder="Tell us about yourself..."
                          maxLength={500}
                          className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                        <p className="text-sm text-neutral-500 mt-1">{profileData.bio.length}/500 characters</p>
                      </div>
                      <div className="border-t border-neutral-700 pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-neutral-300 mb-2">
                              Account Created
                            </label>
                            <input
                              type="text"
                              value={profileData.createdAt ? new Date(profileData.createdAt).toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              }) : 'N/A'}
                              disabled
                              className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-neutral-400 cursor-not-allowed"
                            />
                            <p className="text-sm text-neutral-500 mt-1">Account creation date (read-only)</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleSave('profile')}
                          disabled={isLoading}
                          className="bg-cyan-600 hover:bg-cyan-700 disabled:bg-neutral-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                        >
                          {isLoading ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'security' && (
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-6">Security Settings</h2>
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-medium text-white mb-4">Change Password</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-neutral-300 mb-2">
                              Current Password
                            </label>
                            <input
                              type="password"
                              value={passwordData.currentPassword}
                              onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                              placeholder="Enter current password"
                              className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-neutral-300 mb-2">
                              New Password
                            </label>
                            <input
                              type="password"
                              value={passwordData.newPassword}
                              onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                              placeholder="Enter new password"
                              className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-neutral-300 mb-2">
                              Confirm New Password
                            </label>
                            <input
                              type="password"
                              value={passwordData.confirmPassword}
                              onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                              placeholder="Confirm new password"
                              className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleSave('security')}
                          disabled={isLoading}
                          className="bg-cyan-600 hover:bg-cyan-700 disabled:bg-neutral-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                        >
                          {isLoading ? 'Saving...' : 'Update Password'}
                        </button>
                      </div>

                      {/* Delete Account Section */}
                      {user?.role !== 'ADMIN' && (
                        <div className="mt-8 pt-8 border-t border-neutral-700">
                          <h3 className="text-lg font-medium text-white mb-4">Delete Account</h3>
                          <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 mb-4">
                            <p className="text-red-300 text-sm mb-2">
                              <strong>Warning:</strong> This action cannot be undone.
                            </p>
                            <p className="text-red-300 text-sm">
                              All your data including trades, accounts, preferences, and subscription information will be permanently deleted.
                            </p>
                          </div>
                          <button
                            onClick={() => setShowDeleteAccountModal(true)}
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                          >
                            Delete My Account
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'notifications' && (
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-6">Notification Settings</h2>
                    <div className="space-y-6">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-white font-medium">Email Notifications</h3>
                            <p className="text-sm text-neutral-400">Receive notifications via email</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                              type="checkbox" 
                              className="sr-only peer" 
                              checked={notificationPreferences.emailNotifications}
                              onChange={(e) => setNotificationPreferences(prev => ({ ...prev, emailNotifications: e.target.checked }))}
                            />
                            <div className="w-11 h-6 bg-neutral-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                          </label>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-white font-medium">Order Notifications</h3>
                            <p className="text-sm text-neutral-400">Get notified when orders are placed</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                              type="checkbox" 
                              className="sr-only peer" 
                              checked={notificationPreferences.orderNotifications}
                              onChange={(e) => setNotificationPreferences(prev => ({ ...prev, orderNotifications: e.target.checked }))}
                            />
                            <div className="w-11 h-6 bg-neutral-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                          </label>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-white font-medium">Trade Notifications</h3>
                            <p className="text-sm text-neutral-400">Get notified when trades are executed</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                              type="checkbox" 
                              className="sr-only peer" 
                              checked={notificationPreferences.tradeNotifications}
                              onChange={(e) => setNotificationPreferences(prev => ({ ...prev, tradeNotifications: e.target.checked }))}
                            />
                            <div className="w-11 h-6 bg-neutral-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                          </label>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-white font-medium">Weekly Summary</h3>
                            <p className="text-sm text-neutral-400">Receive weekly trading summary</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                              type="checkbox" 
                              className="sr-only peer" 
                              checked={notificationPreferences.weeklySummary}
                              onChange={(e) => setNotificationPreferences(prev => ({ ...prev, weeklySummary: e.target.checked }))}
                            />
                            <div className="w-11 h-6 bg-neutral-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                          </label>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-white font-medium">System Alerts</h3>
                            <p className="text-sm text-neutral-400">Receive system and security alerts</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                              type="checkbox" 
                              className="sr-only peer" 
                              checked={notificationPreferences.systemAlerts}
                              onChange={(e) => setNotificationPreferences(prev => ({ ...prev, systemAlerts: e.target.checked }))}
                            />
                            <div className="w-11 h-6 bg-neutral-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                          </label>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleSave('notifications')}
                          disabled={isLoading}
                          className="bg-cyan-600 hover:bg-cyan-700 disabled:bg-neutral-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                        >
                          {isLoading ? 'Saving...' : 'Save Preferences'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'trading' && (
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-6">Trading Preferences</h2>
                    
                    {/* PDT Status - First line in Trading section */}
                    {user?.role !== 'ADMIN' && (
                      <div className="mb-6">
                        {isLoadingPDTStatus ? (
                          <div className="bg-neutral-800 border border-neutral-600 rounded-lg p-4">
                            <div className="flex items-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-500 mr-3"></div>
                              <span className="text-neutral-400 text-sm">Loading PDT status...</span>
                            </div>
                          </div>
                        ) : pdtStatus ? (
                          <div className={`border rounded-lg p-4 ${
                            pdtStatus.isPDTQualified
                              ? 'bg-green-900/20 border-green-500/50'
                              : 'bg-yellow-900/20 border-yellow-500/50'
                          }`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                {pdtStatus.isPDTQualified ? (
                                  <svg className="h-5 w-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                ) : (
                                  <svg className="h-5 w-5 text-yellow-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                )}
                                <div>
                                  <p className={`font-medium ${
                                    pdtStatus.isPDTQualified ? 'text-green-300' : 'text-yellow-300'
                                  }`}>
                                    {pdtStatus.statusText}
                                  </p>
                                  <p className="text-sm text-neutral-400 mt-1">
                                    Account Equity: ${pdtStatus.equity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} 
                                    {!pdtStatus.isPDTQualified && (
                                      <span className="ml-2">
                                        (Minimum required: ${pdtStatus.minRequired.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                                      </span>
                                    )}
                                  </p>
                                  {pdtStatus.accountName && (
                                    <p className="text-xs text-neutral-500 mt-1">
                                      Account: {pdtStatus.accountName}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={loadPDTStatus}
                                className="text-cyan-400 hover:text-cyan-300 text-sm font-medium"
                              >
                                Refresh
                              </button>
                            </div>
                            {pdtStatus.error && (
                              <p className="text-xs text-red-400 mt-2">{pdtStatus.error}</p>
                            )}
                          </div>
                        ) : (
                          <div className="bg-neutral-800 border border-neutral-600 rounded-lg p-4">
                            <p className="text-neutral-400 text-sm">
                              Unable to load PDT status. Please ensure you have an active Alpaca account configured.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Alpaca Keys Notice - only show if user has NO active accounts */}
                    {user?.role !== 'ADMIN' && !hasActiveAlpacaAccount && (
                      <div className="mb-6 bg-yellow-900/20 border border-yellow-500/50 rounded-lg p-4">
                        <div className="flex">
                          <svg className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <div className="ml-3">
                            <p className="text-sm text-yellow-300">
                              <strong>Need Alpaca Keys:</strong> These trading preferences will only take effect once you add your Alpaca API keys. 
                              <Link to="/alpaca-keys" className="text-cyan-400 hover:text-cyan-300 underline ml-1">Add them here</Link> to start automated trading.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {isLoadingPreferences ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
                        <span className="ml-3 text-neutral-400">Loading preferences...</span>
                      </div>
                    ) : (
                    <div className="space-y-6">
                      <div className="space-y-8">
                        <div>
                          <label className="block text-sm font-medium text-neutral-300 mb-3">
                            Risk Tolerance
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {(['LOW', 'MEDIUM', 'HIGH'] as const).map((tier) => (
                              <label
                                key={tier}
                                className={`relative flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                  tradingPreferences.riskTolerancePreset === tier
                                    ? 'border-cyan-500 bg-cyan-900/20'
                                    : 'border-neutral-600 bg-neutral-800 hover:border-neutral-500'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="riskTolerance"
                                  value={tier}
                                  checked={tradingPreferences.riskTolerancePreset === tier}
                                  onChange={(e) => setTradingPreferences(prev => ({ ...prev, riskTolerancePreset: e.target.value as 'LOW' | 'MEDIUM' | 'HIGH' }))}
                                  className="sr-only"
                                />
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-white font-medium">{tier}</span>
                                  {tradingPreferences.riskTolerancePreset === tier && (
                                    <svg className="w-5 h-5 text-cyan-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                                <p className="text-xs text-neutral-400">
                                  {RISK_TOLERANCE_DETAILS[tier].description}
                                </p>
                                {tradingPreferences.resolvedValues && (
                                  <div className="mt-3 pt-3 border-t border-neutral-700 text-xs text-neutral-500 space-y-1">
                                    <div>Max Positions: {RISK_TOLERANCE_DETAILS[tier].maxPositions}</div>
                                    <div>Position Cap: {RISK_TOLERANCE_DETAILS[tier].positionCap}</div>
                                    <div>Order Size: {RISK_TOLERANCE_DETAILS[tier].orderSize}</div>
                                  </div>
                                )}
                              </label>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-neutral-300 mb-3">
                            Profit Target
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {(['LOW', 'MEDIUM', 'HIGH'] as const).map((tier) => (
                              <label
                                key={tier}
                                className={`relative flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                  tradingPreferences.profitTargetPreset === tier
                                    ? 'border-cyan-500 bg-cyan-900/20'
                                    : 'border-neutral-600 bg-neutral-800 hover:border-neutral-500'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="profitTarget"
                                  value={tier}
                                  checked={tradingPreferences.profitTargetPreset === tier}
                                  onChange={(e) => setTradingPreferences(prev => ({ ...prev, profitTargetPreset: e.target.value as 'LOW' | 'MEDIUM' | 'HIGH' }))}
                                  className="sr-only"
                                />
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-white font-medium">
                                    {tradingPreferences.presetValues?.profitTargetPct?.[tier]?.toFixed(1) ?? (tier === 'LOW' ? '6.0' : tier === 'MEDIUM' ? '5.0' : '4.0')}%
                                  </span>
                                  {tradingPreferences.profitTargetPreset === tier && (
                                    <svg className="w-5 h-5 text-cyan-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                                <p className="text-xs text-neutral-400">
                                  {tier === 'LOW' && 'Take profits earlier to lock gains'}
                                  {tier === 'MEDIUM' && 'Standard profit objective'}
                                  {tier === 'HIGH' && 'Let winners run longer before taking profits'}
                                </p>
                              </label>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-neutral-300 mb-3">
                            Stop Loss
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {(['LOW', 'MEDIUM', 'HIGH'] as const).map((tier) => (
                              <label
                                key={tier}
                                className={`relative flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                  tradingPreferences.stopLossPreset === tier
                                    ? 'border-cyan-500 bg-cyan-900/20'
                                    : 'border-neutral-600 bg-neutral-800 hover:border-neutral-500'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="stopLoss"
                                  value={tier}
                                  checked={tradingPreferences.stopLossPreset === tier}
                                  onChange={(e) => setTradingPreferences(prev => ({ ...prev, stopLossPreset: e.target.value as 'LOW' | 'MEDIUM' | 'HIGH' }))}
                                  className="sr-only"
                                />
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-white font-medium">
                                    {tradingPreferences.presetValues?.stopLossPct?.[tier]?.toFixed(1) ?? (tier === 'LOW' ? '2.0' : tier === 'MEDIUM' ? '2.5' : '3.0')}%
                                  </span>
                                  {tradingPreferences.stopLossPreset === tier && (
                                    <svg className="w-5 h-5 text-cyan-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                                <p className="text-xs text-neutral-400">
                                  {tier === 'LOW' && 'Tighter stops for faster risk cut'}
                                  {tier === 'MEDIUM' && 'Standard protective stop'}
                                  {tier === 'HIGH' && 'Wider stops to avoid noise whipsaws'}
                                </p>
                              </label>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-neutral-300 mb-2">
                            Check Interval (minutes)
                          </label>
                          <input
                            type="number"
                            value={tradingPreferences.checkInterval}
                            onChange={(e) => setTradingPreferences(prev => ({ ...prev, checkInterval: Number(e.target.value) }))}
                            min="5"
                            max="60"
                            className="w-full md:w-48 bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          />
                          <p className="text-xs text-neutral-500 mt-2">How often the system checks for trading opportunities</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-neutral-300 mb-2">
                            Stock Selection Strategy
                          </label>
                          <div className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-neutral-400">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-white font-medium">AI-Driven Trading Universe</p>
                                <p className="text-xs text-neutral-500 mt-1">
                                  Automatically selects high-quality stocks using multi-factor analysis, market regime detection, and sector diversification
                                </p>
                              </div>
                              <span className="px-2 py-1 bg-cyan-900/30 text-cyan-300 rounded text-xs font-medium">
                                Active
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Biweekly Rotation Section */}
                        {accounts.length > 0 && (
                          <div className="border-t border-neutral-700 pt-8 mt-8">
                            <div className="mb-4">
                              <label className="block text-sm font-medium text-neutral-300 mb-2">
                                Account Selection
                              </label>
                              <select
                                value={selectedAccountId || ''}
                                onChange={(e) => setSelectedAccountId(e.target.value)}
                                className="w-full md:w-64 bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                              >
                                {accounts.map((acc) => (
                                  <option key={acc.id} value={acc.id}>
                                    {acc.displayName || acc.name || acc.id}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <h3 className="text-lg font-semibold text-white mb-4">Biweekly Portfolio Rotation</h3>
                            
                            {isLoadingBiweekly ? (
                              <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-500"></div>
                                <span className="ml-3 text-neutral-400">Loading rotation settings...</span>
                              </div>
                            ) : biweeklyRotation ? (
                              <div className="space-y-6">
                                {/* Enable/Disable Toggle */}
                                <div className="flex items-center justify-between p-4 bg-neutral-800 rounded-lg border border-neutral-700">
                                  <div>
                                    <h4 className="text-white font-medium">Enable Biweekly Rotation</h4>
                                    <p className="text-sm text-neutral-400 mt-1">
                                      Automatically rotate your portfolio every 14 days to optimize performance
                                    </p>
                                  </div>
                                  <label className="relative inline-flex items-center cursor-pointer">
                                    <input 
                                      type="checkbox" 
                                      className="sr-only peer"
                                      checked={biweeklyRotation.enabled}
                                      onChange={(e) => updateBiweeklyRotationPreference(e.target.checked)}
                                      disabled={isLoading}
                                    />
                                    <div className="w-11 h-6 bg-neutral-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                                  </label>
                                </div>

                                {/* Schedule Information */}
                                {biweeklyRotation.schedule && (
                                  <div className="p-4 bg-neutral-800 rounded-lg border border-neutral-700">
                                    <h4 className="text-white font-medium mb-3">Rotation Schedule</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                      <div>
                                        <p className="text-neutral-400">Next Rotation</p>
                                        <p className="text-white font-medium mt-1">
                                          {biweeklyRotation.schedule.nextRotation?.formatted || 'Not scheduled'}
                                        </p>
                                        {biweeklyRotation.schedule.nextRotation && (
                                          <p className="text-xs text-neutral-500 mt-1">
                                            {biweeklyRotation.schedule.nextRotation.daysUntil > 0 
                                              ? `${biweeklyRotation.schedule.nextRotation.daysUntil} days, ${biweeklyRotation.schedule.nextRotation.hoursUntil} hours`
                                              : 'Today'}
                                          </p>
                                        )}
                                      </div>
                                      <div>
                                        <p className="text-neutral-400">Last Rotation</p>
                                        <p className="text-white font-medium mt-1">
                                          {biweeklyRotation.schedule.lastRotation?.formatted || 'Never'}
                                        </p>
                                      </div>
                                      <div>
                                        <p className="text-neutral-400">Frequency</p>
                                        <p className="text-white font-medium mt-1">
                                          Every {biweeklyRotation.schedule.cadenceDays} days ({biweeklyRotation.schedule.day} at {biweeklyRotation.schedule.time} {biweeklyRotation.schedule.timezone?.split('/')[1] || 'ET'})
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Parameters */}
                                {biweeklyRotation.parameters && (
                                  <div className="p-4 bg-neutral-800 rounded-lg border border-neutral-700">
                                    <h4 className="text-white font-medium mb-3">Rotation Parameters</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                      <div>
                                        <p className="text-neutral-400">Exit Fraction</p>
                                        <p className="text-white font-medium mt-1">{biweeklyRotation.parameters.exitFractionDisplay}</p>
                                        <p className="text-xs text-neutral-500 mt-1">Bottom performers to exit</p>
                                      </div>
                                      <div>
                                        <p className="text-neutral-400">Refill Fraction</p>
                                        <p className="text-white font-medium mt-1">{biweeklyRotation.parameters.refillFractionDisplay}</p>
                                        <p className="text-xs text-neutral-500 mt-1">New positions to add</p>
                                      </div>
                                      <div>
                                        <p className="text-neutral-400">Max Turnover</p>
                                        <p className="text-white font-medium mt-1">{biweeklyRotation.parameters.maxTurnoverDisplay}</p>
                                        <p className="text-xs text-neutral-500 mt-1">Per rotation period</p>
                                      </div>
                                      <div>
                                        <p className="text-neutral-400">Cost Threshold</p>
                                        <p className="text-white font-medium mt-1">{biweeklyRotation.parameters.costThresholdDisplay}</p>
                                        <p className="text-xs text-neutral-500 mt-1">Trading cost awareness</p>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Guardrails */}
                                {biweeklyRotation.guardrails && (
                                  <div className="p-4 bg-neutral-800 rounded-lg border border-neutral-700">
                                    <h4 className="text-white font-medium mb-3">Guardrails</h4>
                                    <p className="text-xs text-neutral-400 mb-3 italic">{biweeklyRotation.guardrails.description}</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                      <div>
                                        <p className="text-neutral-400">Single Stock Limit</p>
                                        <p className="text-white font-medium mt-1">{biweeklyRotation.guardrails.singleStockLimitDisplay}</p>
                                      </div>
                                      <div>
                                        <p className="text-neutral-400">Sector Limit</p>
                                        <p className="text-white font-medium mt-1">{biweeklyRotation.guardrails.sectorLimitDisplay}</p>
                                      </div>
                                      <div>
                                        <p className="text-neutral-400">Max Concurrent Positions</p>
                                        <p className="text-white font-medium mt-1">{biweeklyRotation.guardrails.maxConcurrentPositions}</p>
                                      </div>
                                      <div>
                                        <p className="text-neutral-400">Min Cash Buffer</p>
                                        <p className="text-white font-medium mt-1">{biweeklyRotation.guardrails.minCashBufferDisplay}</p>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* What Will Be Traded */}
                                {biweeklyRotation.whatWillBeTraded && (
                                  <div className="p-4 bg-neutral-800 rounded-lg border border-neutral-700">
                                    <h4 className="text-white font-medium mb-3">What Will Be Traded</h4>
                                    <div className="space-y-4 text-sm">
                                      <div>
                                        <p className="text-neutral-300 font-medium mb-2">Exits:</p>
                                        <p className="text-neutral-400">{biweeklyRotation.whatWillBeTraded.exitCriteria.description}</p>
                                        <p className="text-xs text-neutral-500 mt-1 italic">{biweeklyRotation.whatWillBeTraded.exitCriteria.example}</p>
                                      </div>
                                      <div>
                                        <p className="text-neutral-300 font-medium mb-2">Refills:</p>
                                        <p className="text-neutral-400">{biweeklyRotation.whatWillBeTraded.refillCriteria.description}</p>
                                        <p className="text-xs text-neutral-500 mt-1 italic">{biweeklyRotation.whatWillBeTraded.refillCriteria.example}</p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-center py-8 text-neutral-400">
                                No rotation settings available. Please ensure you have an active account.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-white font-medium">Paper Trading Only</h3>
                          <p className="text-sm text-neutral-400">Restrict all trades to paper trading mode</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input 
                            type="checkbox" 
                            className="sr-only peer"
                            checked={tradingPreferences.paperTradingOnly}
                            onChange={(e) => setTradingPreferences(prev => ({ ...prev, paperTradingOnly: e.target.checked }))}
                          />
                          <div className="w-11 h-6 bg-neutral-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                        </label>
                      </div>
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleSave('trading')}
                          disabled={isLoading}
                          className="bg-cyan-600 hover:bg-cyan-700 disabled:bg-neutral-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                        >
                          {isLoading ? 'Saving...' : 'Save Preferences'}
                        </button>
                      </div>
                    </div>
                    )}
                  </div>
                )}

                {activeTab === 'api' && (
                  <div>
                    <h2 className="text-xl font-semibold text-white mb-6">API Management</h2>
                    <div className="space-y-6">
                      <div className="bg-blue-900/20 border border-blue-500/50 rounded-lg p-4">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-400">API Access</h3>
                            <div className="mt-2 text-sm text-blue-300">
                              <p>
                                Manage your API keys and access tokens. For security reasons, 
                                API keys are managed separately in the Alpaca Keys section.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="text-center py-8">
                        <div className="text-neutral-400 mb-4">
                          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-medium text-white mb-2">API Keys</h3>
                        <p className="text-neutral-400 mb-4">
                          Manage your trading account API keys and credentials
                        </p>
                        <Link
                          to="/alpaca-keys"
                          className="inline-flex items-center px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-colors"
                        >
                          Manage API Keys
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteAccountModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-70">
          <div className="bg-neutral-900 p-6 rounded-lg shadow-xl w-full max-w-md border border-neutral-700">
            <h3 className="text-xl font-bold text-white mb-4">Delete Account</h3>
            <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 mb-4">
              <p className="text-red-300 text-sm mb-2">
                <strong>Warning:</strong> This action cannot be undone.
              </p>
              <p className="text-red-300 text-sm">
                All your data including trades, accounts, preferences, and subscription information will be permanently deleted.
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-300 mb-2">
                Type <strong>DELETE</strong> to confirm
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            {message && message.type && (
              <p className={`text-sm mb-4 ${message.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                {message.text}
              </p>
            )}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteAccountModal(false);
                  setDeleteConfirmText('');
                  setMessage(null);
                }}
                disabled={isDeleting}
                className="px-4 py-2 border border-neutral-600 hover:border-neutral-500 text-neutral-300 hover:text-white rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (deleteConfirmText !== 'DELETE') {
                    setMessage({ type: 'error', text: 'Please type DELETE to confirm' });
                    return;
                  }

                  setIsDeleting(true);
                  setMessage(null);

                  try {
                    const response = await deleteUserAccount();

                    if (response.success) {
                      setMessage({ type: 'success', text: 'Account deleted successfully. Redirecting...' });
                      // Clear auth token and redirect to home
                      localStorage.removeItem('auth_token');
                      setTimeout(() => {
                        window.location.href = '/';
                      }, 2000);
                    } else {
                      setMessage({ type: 'error', text: typeof response.error === 'string' ? response.error : 'Failed to delete account' });
                      setIsDeleting(false);
                    }
                  } catch (err: any) {
                    console.error('Error deleting account:', err);
                    setMessage({ type: 'error', text: err?.response?.data?.error || err?.message || 'Failed to delete account' });
                    setIsDeleting(false);
                  }
                }}
                disabled={isDeleting || deleteConfirmText !== 'DELETE'}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-neutral-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                {isDeleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
            {isDeleting && (
              <div className="mt-4 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-400 mx-auto"></div>
                <p className="text-sm text-neutral-400 mt-2">Deleting account...</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
